
import { defineComponent, ref, watch, computed } from 'vue';

export default defineComponent({
  name: 'PromptDialog',
  props: {
    value: {
      type: Boolean,
      required: true
    },
    searchData: {
      type: Object,
      default: () => ({})
    }
  },
  setup(props, { emit }) {
    const showDialog = computed({
      get: () => props.value,
      set: value => emit('input', value)
    });

    const openAIResponse = ref('');
    const errorMessage = ref('');
    const isValidResponse = ref(false);
    const parsedWebsites = ref([]);

    const searchCriteria = computed(() => {
      if (!props.searchData) return '';
      const { role, subject, location } = props.searchData;

      // Handle location object
      let locationText = '';
      if (location) {
        if (typeof location === 'string') {
          locationText = location;
        } else if (location.text) {
          locationText = location.text;
        } else if (location.value) {
          locationText = location.value.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
        }
      }

      return `Role: ${role}\nSubject: ${subject}\nLocation: ${locationText}`;
    });

    const openAIPrompt = computed(() => {
      if (!props.searchData) return '';
      const { role, subject, location, frameworks } = props.searchData;
      if (!role || !subject || !location || !frameworks) return '';

      // Handle location object
      let locationText = '';
      if (location) {
        if (typeof location === 'string') {
          locationText = location;
        } else if (location.text) {
          locationText = location.text;
        } else if (location.value) {
          locationText = location.value.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
        }
      }

      return `Find the top employer's website URL for each of these organization types in ${locationText}. Return ONLY the JSON array of objects with the following format with no before or after text:

[
  {
    "framework_id": "school_district",
    "website": "https://www.schools.nyc.gov"
  },
  {
    "framework_id": "hospital",
    "website": "https://www.nychealthandhospitals.org"
  }
]

Organization types to find (with their IDs):
${frameworks.map(f => `${f.organization_type_name} (ID: ${f._id})`).join('\n')}`;
    });

    const handleResponseInput = () => {
      try {
        // Parse the JSON response
        const responseData = JSON.parse(openAIResponse.value);

        if (!Array.isArray(responseData)) {
          throw new Error('Response must be an array');
        }

        // Validate each item has required fields
        const validData = responseData.every(
          item =>
            item.framework_id &&
            item.website &&
            (item.website.startsWith('http://') || item.website.startsWith('https://'))
        );

        if (!validData) {
          throw new Error('Invalid response format');
        }

        parsedWebsites.value = responseData;
        errorMessage.value = '';
        isValidResponse.value = true;
      } catch (error) {
        errorMessage.value = `Invalid response format: ${error.message}`;
        isValidResponse.value = false;
        console.error('Error processing response:', error);
      }
    };

    const handleProceed = () => {
      if (!isValidResponse.value || !parsedWebsites.value.length) {
        errorMessage.value = 'Please enter a valid response';
        return;
      }

      emit('proceed', parsedWebsites.value);
      showDialog.value = false;
    };

    const copyToClipboard = text => {
      navigator.clipboard
        .writeText(text)
        .then(() => {
          // You could add a toast notification here if desired
        })
        .catch(err => {
          console.error('Failed to copy text: ', err);
        });
    };

    // Watch for changes in the response
    watch(openAIResponse, () => {
      handleResponseInput();
    });

    return {
      showDialog,
      openAIResponse,
      errorMessage,
      isValidResponse,
      handleProceed,
      handleResponseInput,
      copyToClipboard,
      searchCriteria,
      openAIPrompt,
      generatePrompts: () => {
        if (!props.searchData) return '';

        const { role, subject, location, frameworks } = props.searchData;
        if (!role || !subject || !location || !frameworks) return '';

        return `Find the top employer's website URL for each of these organization types in ${location}:

${frameworks.map(f => f.organization_type_name).join('\n')}

Return a JSON array of strings containing just the website URLs, like this:
[
  "https://example.com/org1",
  "https://example.com/org2"
]`;
      }
    };
  }
});
